import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, Check, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userData, setUserData] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError("Enlace inválido");
        setLoading(false);
        return;
      }
      
      try {
        const res = await axios.get(`${API}/auth/verify-token/${token}`);
        setUserData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "El enlace ha expirado o es inválido");
      } finally {
        setLoading(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/auth/set-password`, { token, password });
      setSuccess(true);
      toast.success("Contraseña configurada exitosamente");
      
      setTimeout(() => {
        navigate("/panel-gestion-8k2m/acceso");
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al configurar contraseña");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <div className="bg-[#121212] border border-white/5 p-8 rounded-lg max-w-md w-full text-center">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h1 className="text-xl text-white mb-2">Enlace Inválido</h1>
          <p className="text-[#A3A3A3] mb-6">{error}</p>
          <Button onClick={() => navigate("/panel-gestion-8k2m/acceso")} className="bg-[#D4AF37] text-black hover:bg-[#FFE082]">
            Ir al Login
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <div className="bg-[#121212] border border-white/5 p-8 rounded-lg max-w-md w-full text-center">
          <Check size={48} className="mx-auto text-green-500 mb-4" />
          <h1 className="text-xl text-white mb-2">¡Contraseña Configurada!</h1>
          <p className="text-[#A3A3A3]">Redirigiendo al login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="bg-[#121212] border border-white/5 p-8 rounded-lg max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
            <Lock className="text-[#D4AF37]" size={32} />
          </div>
          <h1 className="text-2xl text-white mb-2">Configura tu Contraseña</h1>
          <p className="text-[#A3A3A3]">
            Hola <span className="text-white font-medium">{userData?.name}</span>, configura tu contraseña para acceder al panel.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white">Nueva Contraseña</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#1a1a1a] border-white/10 text-white"
              placeholder="Mínimo 8 caracteres"
              required
              minLength={8}
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-white">Confirmar Contraseña</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="bg-[#1a1a1a] border-white/10 text-white"
              placeholder="Repite la contraseña"
              required
            />
          </div>
          
          <Button
            type="submit"
            className="w-full bg-[#D4AF37] text-black hover:bg-[#FFE082] py-6"
            disabled={submitting}
          >
            {submitting ? "Guardando..." : "Guardar Contraseña"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default SetPasswordPage;
